import "@blueprintjs/datetime/lib/css/blueprint-datetime.css"
import React, {useEffect, useRef, useState} from "react"
import {useDispatch, useSelector} from "react-redux"
import {Card, FormGroup, NumericInput, Switch} from "@blueprintjs/core"
import moment from "moment"
import _ from "lodash"
import Props from "../../../core/components/props"
import "./FilterOptionPanel.scss"
import Filter from "../../models/filter"
import StringSelect from "../../../core/components/StringSelect"
import {
    dataClean,
    loadConfigurationAction,
    loadExistingVariablesAction,
    setDisplayDataContextAction,
} from "../../redux/refdb.actions"
import {
    assaySelector,
    existingVariablesSelector,
    getDisplayData,
    getRefDbDataContext,
} from "../../redux/refdb.selector"
import handleBooleanChange from "../../../core/utils/handleChangehelpers"
import {
    IMultiSelectItemState,
    MultiSelectString,
} from "../../../core/components/MultiSelectString"
import {
    Filters,
    genderMap,
    VariableTypeEnum,
    VariableTypeProperties,
} from "../../../shared/enums/variableTypeEnum"
import DatePickerDialog from "../../../core/components/DatePickerDialog"
import useComponentWillMount from "../../../core/utils/componentUtils"
import SwitchableValue from "../../models/switchableValue"

interface StringSelectProps extends Props {
    selectedValue?: string
    values?: string[]
    setSelectedValue?: (value: string) => void
    disabled?: boolean
}

const FilterOptionPanel: React.FC<StringSelectProps> = (props) => {
    const contextData = useSelector(getRefDbDataContext)
    const assay = useSelector(assaySelector)
    const displayData = useSelector(getDisplayData)
    const existingVariables = useSelector(existingVariablesSelector)

    const [filters, setFilters] = useState<Filter[]>(
        _.cloneDeep(contextData.filters) || _.cloneDeep(Object.values(Filters))
    )
    const dispatch = useDispatch()

    const justOpen = useRef(true)

    useEffect(() => {
        console.log("Dispatch context to redux...")
        const newContext = {...contextData, filters}
        dispatch(setDisplayDataContextAction(newContext))
        if (!justOpen.current) {
            dispatch(dataClean())
        } else {
            justOpen.current = false
        }
    }, [filters])

    const updateFilterValue = (index: number, value: any) => {
        const list = [...filters]
        list[index].value = value
        setFilters(list)
    }

    const updateFilterValueFrom = (index: number, value: any) => {
        const list = [...filters]
        list[index].from = value
        setFilters(list)
    }

    const updateFilterValueTo = (index: number, value: any) => {
        const list = [...filters]
        list[index].to = value
        setFilters(list)
    }
    const updateFilterEnable = (index: number, value: any) => {
        const list = [...filters]
        list[index].enable = value
        setFilters(list)
    }

    useComponentWillMount(() => {
        dispatch(loadExistingVariablesAction())
        dispatch(loadConfigurationAction("FITNESS"))
        dispatch(loadConfigurationAction("ASSAY"))
        dispatch(loadConfigurationAction("ETHNICITY"))
        dispatch(loadConfigurationAction("NUTRITION"))
        dispatch(loadConfigurationAction("ALCOHOL"))
        dispatch(loadConfigurationAction("SMOKING"))
    })

    const handleAssaySelect = (e: IMultiSelectItemState) => {
        const newContext = {
            ...contextData,
            assay: {value: e.selected, enable: contextData.assay.enable},
            classes: SwitchableValue.disable([]),
            metabolites: SwitchableValue.disable([]),
        }
        dispatch(dataClean())
        dispatch(setDisplayDataContextAction(newContext))
    }

    const updateAssayEnable = (value: any) => {
        const newContext = {
            ...contextData,
            assay: {value: contextData.assay.value, enable: value},
            classes: SwitchableValue.disable([]),
            metabolites: SwitchableValue.disable([]),
        }
        dispatch(dataClean())
        dispatch(setDisplayDataContextAction(newContext))
    }

    const ownSamplesRestriction = (e: any) => {
        const newContext = {...contextData, ownSamples: e}
        dispatch(dataClean())
        dispatch(setDisplayDataContextAction(newContext))
    }

    const disabled = (name: string) => {
        const result = !_.has(existingVariables, name)
        return result
    }

    return (
        <Card>
            <div className="CardRow">
                <Switch
                    className="CardRowElement"
                    labelElement="Restriction to own samples"
                    checked={contextData.ownSamples}
                    disabled={disabled("Restriction to own samples")}
                    onChange={handleBooleanChange((value) =>
                        ownSamplesRestriction(value)
                    )}
                />
            </div>
            <div className="CardRow">
                <Switch
                    className="CardRowElement"
                    checked={contextData.assay.enable}
                    labelElement="Restrict assay"
                    onChange={handleBooleanChange((value) =>
                        updateAssayEnable(value)
                    )}
                />

                <MultiSelectString
                    disabled={!contextData.assay.enable}
                    selected={contextData.assay.value}
                    items={assay || []}
                    handleSelect={handleAssaySelect}
                    name="assay"
                />
            </div>
            <div className="CardRow">
                <Switch
                    className="CardRowElement"
                    checked={filters[VariableTypeEnum.date].enable}
                    labelElement="Use samples present at date "
                    onChange={handleBooleanChange((value) => {
                        updateFilterEnable(VariableTypeEnum.date, value)
                        if (
                            filters[VariableTypeEnum.date].enable &&
                            !filters[VariableTypeEnum.date].value
                        ) {
                            filters[VariableTypeEnum.date].value = new Date()
                        }
                    })}
                />
                {filters[VariableTypeEnum.date].value &&
                    moment(filters[VariableTypeEnum.date].value).format(
                        "DD-MM-YYYY"
                    )}
                <DatePickerDialog
                    disabled={!filters[VariableTypeEnum.date].enable}
                    setSelectedValue={(value) =>
                        updateFilterValue(VariableTypeEnum.date, value)
                    }
                    selectedValue={
                        filters[VariableTypeEnum.date].value || undefined
                    }
                />
            </div>

            <div className="CardRow">
                <Switch
                    className="CardRowElement"
                    checked={filters[VariableTypeEnum.gender].enable}
                    labelElement="Sex"
                    onChange={handleBooleanChange((value) =>
                        updateFilterEnable(VariableTypeEnum.gender, value)
                    )}
                />
                <StringSelect
                    disabled={!filters[VariableTypeEnum.gender].enable}
                    className="CardRowElement2"
                    id="sample-source-select"
                    values={["male", "female"]}
                    selectedValue={genderMap.get(
                        filters[VariableTypeEnum.gender].value
                    )}
                    setSelectedValue={(value) =>
                        updateFilterValue(
                            VariableTypeEnum.gender,
                            genderMap.get(value)
                        )
                    }
                />
            </div>
            <div className="CardRow">
                <FormGroup>
                    <Switch
                        className="CardRowElement2"
                        checked={filters[VariableTypeEnum.age].enable}
                        labelElement="Age [years]"
                        onChange={handleBooleanChange((value) => {
                            updateFilterEnable(VariableTypeEnum.age, value)
                        })}
                    />
                </FormGroup>
                <FormGroup inline label="between" labelFor="between">
                    <NumericInput
                        disabled={!filters[VariableTypeEnum.age].enable}
                        id="between"
                        value={filters[VariableTypeEnum.age].from}
                        onValueChange={(value) =>
                            updateFilterValueFrom(VariableTypeEnum.age, value)
                        }
                        className="numeric-input"
                    />
                </FormGroup>
                <FormGroup
                    inline
                    label="and"
                    labelFor="and"
                    style={{marginLeft: "-17px"}}
                >
                    <NumericInput
                        disabled={!filters[VariableTypeEnum.age].enable}
                        id="and"
                        value={filters[VariableTypeEnum.age].to}
                        onValueChange={(value) =>
                            updateFilterValueTo(VariableTypeEnum.age, value)
                        }
                        className="numeric-input"
                    />
                </FormGroup>
            </div>
            <div className="CardRow">
                <FormGroup>
                    <Switch
                        className="CardRowElement2"
                        checked={filters[VariableTypeEnum.bmi].enable}
                        labelElement="BMI"
                        onChange={handleBooleanChange((value) => {
                            updateFilterEnable(VariableTypeEnum.bmi, value)
                        })}
                    />
                </FormGroup>
                <FormGroup inline label="between" labelFor="between_bmi">
                    <NumericInput
                        disabled={!filters[VariableTypeEnum.bmi].enable}
                        id="between_bmi"
                        value={filters[VariableTypeEnum.bmi].from}
                        onValueChange={(value) =>
                            updateFilterValueFrom(VariableTypeEnum.bmi, value)
                        }
                        className="numeric-input"
                    />
                </FormGroup>
                <FormGroup
                    inline
                    label="and"
                    labelFor="and_bmi"
                    style={{marginLeft: "-17px"}}
                >
                    <NumericInput
                        disabled={!filters[VariableTypeEnum.bmi].enable}
                        value={filters[VariableTypeEnum.bmi].to}
                        id="and"
                        onValueChange={(value) =>
                            updateFilterValueTo(VariableTypeEnum.bmi, value)
                        }
                        className="numeric-input"
                    />
                </FormGroup>
            </div>
            <div className="CardRow">
                <Switch
                    className="CardRowElement"
                    disabled={disabled(
                        filters[VariableTypeEnum.fasting_status].name
                    )}
                    checked={filters[VariableTypeEnum.fasting_status].enable}
                    labelElement="Fasting status"
                    onChange={handleBooleanChange((value) =>
                        updateFilterEnable(
                            VariableTypeEnum.fasting_status,
                            value
                        )
                    )}
                />
                <StringSelect
                    disabled={!filters[VariableTypeEnum.fasting_status].enable}
                    className="CardRowElement2"
                    id="sample-source-select"
                    values={["fasted", "non-fasted"]}
                    enabledValues={
                        existingVariables
                            ? existingVariables[
                                  filters[VariableTypeEnum.fasting_status].name
                              ]
                            : []
                    }
                    selectedValue={
                        filters[VariableTypeEnum.fasting_status].value
                    }
                    setSelectedValue={(value) =>
                        updateFilterValue(
                            VariableTypeEnum.fasting_status,
                            value
                        )
                    }
                />
            </div>

            <div className="CardRow">
                <Switch
                    className="CardRowElement"
                    disabled={disabled(
                        filters[VariableTypeEnum.ethnicity].name
                    )}
                    checked={filters[VariableTypeEnum.ethnicity].enable}
                    labelElement="Ethnicity/race"
                    onChange={handleBooleanChange((value) =>
                        updateFilterEnable(VariableTypeEnum.ethnicity, value)
                    )}
                />
                <MultiSelectString
                    disabled={!filters[VariableTypeEnum.ethnicity].enable}
                    className="CardRowElement2"
                    name="sample-source-select"
                    items={displayData.contextValues.ethnicity || []}
                    enabledItems={
                        existingVariables
                            ? existingVariables[
                                  filters[VariableTypeEnum.ethnicity].name
                              ]
                            : []
                    }
                    selected={filters[VariableTypeEnum.ethnicity].value}
                    handleSelect={(value) =>
                        updateFilterValue(
                            VariableTypeEnum.ethnicity,
                            value.selected
                        )
                    }
                />
            </div>
            <div className="CardRow">
                <Switch
                    className="CardRowElement"
                    disabled={disabled(filters[VariableTypeEnum.fitness].name)}
                    checked={filters[VariableTypeEnum.fitness].enable}
                    labelElement={filters[VariableTypeEnum.fitness].name}
                    onChange={handleBooleanChange((value) =>
                        updateFilterEnable(VariableTypeEnum.fitness, value)
                    )}
                />
                <MultiSelectString
                    disabled={!filters[VariableTypeEnum.fitness].enable}
                    className="CardRowElement2"
                    name="sample-source-select"
                    items={displayData.contextValues.fitness || []}
                    enabledItems={
                        existingVariables
                            ? existingVariables[
                                  filters[VariableTypeEnum.fitness].name
                              ]
                            : []
                    }
                    selected={filters[VariableTypeEnum.fitness].value}
                    handleSelect={(value) =>
                        updateFilterValue(
                            VariableTypeEnum.fitness,
                            value.selected
                        )
                    }
                />
            </div>
            <div className="CardRow">
                <FormGroup>
                    <Switch
                        className="CardRowElement2"
                        checked={filters[VariableTypeEnum.alcohol].enable}
                        labelElement="Alcoholic drinks/week"
                        onChange={handleBooleanChange((value) => {
                            updateFilterEnable(VariableTypeEnum.alcohol, value)
                        })}
                    />
                </FormGroup>
                <FormGroup inline label="between" labelFor="between_alcohol">
                    <NumericInput
                        disabled={!filters[VariableTypeEnum.alcohol].enable}
                        id="between_alcohol"
                        value={filters[VariableTypeEnum.alcohol].from}
                        onValueChange={(value) =>
                            updateFilterValueFrom(
                                VariableTypeEnum.alcohol,
                                value
                            )
                        }
                        className="numeric-input"
                    />
                </FormGroup>
                <FormGroup
                    inline
                    label="and"
                    labelFor="and_alcohol"
                    style={{marginLeft: "-17px"}}
                >
                    <NumericInput
                        disabled={!filters[VariableTypeEnum.alcohol].enable}
                        value={filters[VariableTypeEnum.alcohol].to}
                        id="and"
                        onValueChange={(value) =>
                            updateFilterValueTo(VariableTypeEnum.alcohol, value)
                        }
                        className="numeric-input"
                    />
                </FormGroup>
            </div>
            <div className="CardRow">
                <Switch
                    className="CardRowElement"
                    disabled={disabled(filters[VariableTypeEnum.smoking].name)}
                    checked={filters[VariableTypeEnum.smoking].enable}
                    labelElement={
                        VariableTypeProperties[VariableTypeEnum.smoking].value
                    }
                    onChange={handleBooleanChange((value) =>
                        updateFilterEnable(VariableTypeEnum.smoking, value)
                    )}
                />
                <MultiSelectString
                    disabled={!filters[VariableTypeEnum.smoking].enable}
                    className="CardRowElement2"
                    name="sample-source-select"
                    items={displayData.contextValues.smoking || []}
                    enabledItems={
                        existingVariables
                            ? existingVariables[
                                  filters[VariableTypeEnum.smoking].name
                              ]
                            : []
                    }
                    selected={filters[VariableTypeEnum.smoking].value}
                    handleSelect={(value) =>
                        updateFilterValue(
                            VariableTypeEnum.smoking,
                            value.selected
                        )
                    }
                />
            </div>
            <div className="CardRow">
                <Switch
                    className="CardRowElement"
                    disabled={disabled(
                        filters[VariableTypeEnum.nutrition].name
                    )}
                    checked={filters[VariableTypeEnum.nutrition].enable}
                    labelElement="Nutrition"
                    onChange={handleBooleanChange((value) =>
                        updateFilterEnable(VariableTypeEnum.nutrition, value)
                    )}
                />

                <MultiSelectString
                    disabled={!filters[VariableTypeEnum.nutrition].enable}
                    className="CardRowElement2"
                    name="sample-source-select"
                    items={displayData.contextValues.nutrition || []}
                    enabledItems={
                        existingVariables
                            ? existingVariables[
                                  filters[VariableTypeEnum.nutrition].name
                              ]
                            : []
                    }
                    selected={filters[VariableTypeEnum.nutrition].value}
                    handleSelect={(value) =>
                        updateFilterValue(
                            VariableTypeEnum.nutrition,
                            value.selected
                        )
                    }
                />
            </div>
        </Card>
    )
}

FilterOptionPanel.defaultProps = {
    disabled: false,
}

export default FilterOptionPanel
